import React from 'react';
import { 
  ExclamationTriangleIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  InformationCircleIcon 
} from '@heroicons/react/24/outline';

const MappingFailureDisplay = ({ mappingStats, className = '' }) => {
  // Don't render if no mapping stats available
  if (!mappingStats || mappingStats.total === 0) {
    return null;
  }

  const { total, successful, failed, successRate } = mappingStats;
  const hasFailures = failed > 0;
  const isHighFailureRate = failed > total * 0.3; // More than 30% failure rate

  const getStatusColor = () => {
    if (failed === 0) return 'green';
    if (isHighFailureRate) return 'red';
    return 'yellow';
  };

  const getStatusIcon = () => {
    const iconClass = "w-5 h-5";
    if (failed === 0) return <CheckCircleIcon className={`${iconClass} text-green-500`} />;
    if (isHighFailureRate) return <XCircleIcon className={`${iconClass} text-red-500`} />;
    return <ExclamationTriangleIcon className={`${iconClass} text-yellow-500`} />;
  };

  const getStatusMessage = () => {
    if (failed === 0) return "All redactions successfully mapped to visual coordinates";
    if (isHighFailureRate) return `High mapping failure rate detected (${failed}/${total} failed)`;
    return `Some redactions could not be visually mapped (${failed}/${total} failed)`;
  };

  const statusColor = getStatusColor();

  return (
    <div className={`${className}`}>
      <div className={`border rounded-lg p-4 ${
        statusColor === 'green' ? 'bg-green-50 border-green-200' :
        statusColor === 'red' ? 'bg-red-50 border-red-200' :
        'bg-yellow-50 border-yellow-200'
      }`}>
        <div className="flex items-start space-x-3">
          {getStatusIcon()}
          <div className="flex-1">
            <h4 className={`font-medium ${
              statusColor === 'green' ? 'text-green-800' :
              statusColor === 'red' ? 'text-red-800' :
              'text-yellow-800'
            }`}>
              Visual Redaction Mapping Status
            </h4>
            <p className={`text-sm mt-1 ${
              statusColor === 'green' ? 'text-green-700' :
              statusColor === 'red' ? 'text-red-700' :
              'text-yellow-700'
            }`}>
              {getStatusMessage()}
            </p>
            
            {/* Detailed Statistics */}
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <div className={`text-lg font-semibold ${
                  statusColor === 'green' ? 'text-green-800' :
                  statusColor === 'red' ? 'text-red-800' :
                  'text-yellow-800'
                }`}>
                  {total}
                </div>
                <div className={`text-xs ${
                  statusColor === 'green' ? 'text-green-600' :
                  statusColor === 'red' ? 'text-red-600' :
                  'text-yellow-600'
                }`}>
                  Visual Redactions
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-lg font-semibold text-green-800">
                  {successful}
                </div>
                <div className="text-xs text-green-600">
                  Successfully Mapped
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-lg font-semibold text-red-800">
                  {failed}
                </div>
                <div className="text-xs text-red-600">
                  Mapping Failed
                </div>
              </div>
              
              <div className="text-center">
                <div className={`text-lg font-semibold ${
                  statusColor === 'green' ? 'text-green-800' :
                  statusColor === 'red' ? 'text-red-800' :
                  'text-yellow-800'
                }`}>
                  {successRate}
                </div>
                <div className={`text-xs ${
                  statusColor === 'green' ? 'text-green-600' :
                  statusColor === 'red' ? 'text-red-600' :
                  'text-yellow-600'
                }`}>
                  Mapping Rate
                </div>
              </div>
            </div>

            {/* Explanation and Actions for Failures */}
            {hasFailures && (
              <div className="mt-4 border-t border-gray-200 pt-3">
                <div className="flex items-start space-x-2">
                  <InformationCircleIcon className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-gray-600">
                    <p className="font-medium text-gray-700 mb-1">Why do mapping failures occur?</p>
                    <ul className="space-y-1 text-gray-600">
                      <li>• Text formatting differences between AI analysis and PDF extraction</li>
                      <li>• OCR errors in scanned documents creating text variations</li>
                      <li>• Complex document layouts with overlapping elements</li>
                      <li>• Coordinate system precision issues during conversion</li>
                    </ul>
                    
                    {isHighFailureRate && (
                      <div className="mt-3 p-3 bg-red-100 border border-red-200 rounded">
                        <p className="font-medium text-red-800">High Failure Rate Warning</p>
                        <p className="text-red-700 text-xs mt-1">
                          This document has significant mapping issues. Consider:
                        </p>
                        <ul className="text-xs text-red-700 mt-1 space-y-1">
                          <li>• Re-uploading a higher quality version of the document</li>
                          <li>• Converting scanned PDFs to searchable text before processing</li>
                          <li>• Manually reviewing redactions that failed to map visually</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MappingFailureDisplay;